import {
  useCallback, useRef, useState, useEffect, useMemo,
} from 'react';
import { TabPanel } from '@mui/lab';
import Problems from 'components/pages/Competition/Problems/Problems';
import Leaderboard from 'components/pages/Competition/Leaderboard/Leaderboard';
import Submissions from 'components/pages/Competition/Submissions/Submissions';
import CompetitionNotion from 'components/pages/Competition/CompetiotionNotion';
import Box from '@mui/material/Box';

export const TabItems = {
  OVERVIEW: 'overview',
  RULES: 'rules',
  PROBLEMS: 'problems',
  LEADERBOARD: 'leaderboard',
  SUBMISSIONS: 'submissions',
};

const CompetitionTabContent = ({ selectedTab, definitionLink, rulesLink }) => {
  const loadedLinksRef = useRef(new Set());
  const [isVisible, setIsVisible] = useState(true);
  const [currentTab, setCurrentTab] = useState(selectedTab);
  const prevTabRef = useRef(selectedTab);

  // Avoid redundant loading state
  const shouldShowLoading = useCallback((link) => {
    if (!link || loadedLinksRef.current.has(link)) return false;
    loadedLinksRef.current.add(link);
    return true;
  }, []);

  // Memoized content components
  const overviewTab = useMemo(() => (
    <CompetitionNotion
      link={definitionLink}
      showLoadingOnFirstRender={shouldShowLoading(definitionLink)}
    />
  ), [definitionLink, shouldShowLoading]);

  const rulesTab = useMemo(() => (
    <CompetitionNotion
      link={rulesLink}
      showLoadingOnFirstRender={shouldShowLoading(rulesLink)}
    />
  ), [rulesLink, shouldShowLoading]);

  const getTabContent = useCallback((tab) => {
    switch (tab) {
      case TabItems.OVERVIEW:
        return overviewTab;
      case TabItems.RULES:
        return rulesTab;
      case TabItems.PROBLEMS:
        return <Problems />;
      case TabItems.LEADERBOARD:
        return <Leaderboard />;
      case TabItems.SUBMISSIONS:
        return <Submissions />;
      default:
        return overviewTab;
    }
  }, [overviewTab, rulesTab]);

  // Handle tab transitions smoothly
  useEffect(() => {
    if (prevTabRef.current === selectedTab) return;

    // Start fade-out
    setIsVisible(false);

    // After fade-out completes, update content & fade in
    const timer = setTimeout(() => {
      setCurrentTab(selectedTab); // Update the displayed tab
      setIsVisible(true);
      prevTabRef.current = selectedTab;
    }, 300); // Ensure it matches transition duration

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [selectedTab]);

  return (
    <TabPanel value={selectedTab} sx={{ padding: '24px 0' }}>
      <Box sx={{
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 300ms ease-in-out',
        minHeight: '200px',
      }}
      >
        {getTabContent(currentTab)}
      </Box>
    </TabPanel>
  );
};

export default CompetitionTabContent;
